import { supabase } from '../lib/supabase';
import { subscriptionService } from './subscriptionService';
import toast from '../components/ui/Toast';

export const agentManagementService = {
  async getAgentStatus(userId, teamId = null) {
    try {
      // Get all conversations for the user
      let query = supabase
        ?.from('agent_conversations')
        ?.select('agent_id, status, updated_at')
        ?.eq('user_id', userId);
      
      if (teamId) {
        query = query?.eq('team_id', teamId);
      }
      
      const { data: conversations, error: convError } = await query?.order('updated_at', { ascending: false });

      if (convError) throw convError;

      // Get agent limit status
      const { data: limitStatus, error: limitError } = await subscriptionService?.getAgentLimitStatus(userId);
      if (limitError) throw limitError;

      // Process agent status data
      const agentMap = new Map();
      
      conversations?.forEach(conv => {
        const agentId = conv?.agent_id;
        if (!agentMap?.has(agentId)) {
          agentMap?.set(agentId, {
            agentId,
            isActive: conv?.status === 'pending' || conv?.status === 'processing',
            lastUsed: conv?.updated_at,
            status: conv?.status
          });
        }
      });

      return { 
        data: {
          agents: Array.from(agentMap?.values()),
          limitStatus
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Error getting agent status:', error);
      toast?.error('Failed to load agent status', {
        description: error?.message || 'Unable to retrieve agent information'
      });
      return { data: null, error };
    }
  },

  async toggleAgent(userId, agentId, activate, teamId = null) {
    try {
      if (activate) {
        // Check if user can activate more agents
        const { data: limitStatus } = await subscriptionService?.getAgentLimitStatus(userId);
        
        if (limitStatus && !limitStatus?.is_compliant) {
          const error = new Error(`Agent limit exceeded. Your plan allows ${limitStatus?.max_agents} active agents.`);
          toast?.warning('Agent limit reached', {
            description: error?.message,
            action: {
              label: 'Upgrade Plan',
              onClick: () => window.location.href = '/pricing'
            },
            duration: 6000
          });
          return { 
            data: null, 
            error: { message: error?.message }
          };
        }

        // Create new conversation to activate agent
        const insertData = {
          user_id: userId,
          agent_id: agentId,
          status: 'pending',
          priority: 'normal'
        };
        
        if (teamId) {
          insertData.team_id = teamId;
        }
        
        const { data, error } = await supabase
          ?.from('agent_conversations')
          ?.insert(insertData)
          ?.select()
          ?.single();

        if (error) throw error;
        return { data, error: null };
      } else {
        // Deactivate agent by updating all active conversations to completed
        let query = supabase
          ?.from('agent_conversations')
          ?.update({ status: 'completed' })
          ?.eq('user_id', userId)
          ?.eq('agent_id', agentId)
          ?.in('status', ['pending', 'processing']);
        
        if (teamId) {
          query = query?.eq('team_id', teamId);
        }
        
        const { data, error } = await query?.select();

        if (error) throw error;
        return { data, error: null };
      }
    } catch (error) {
      console.error('Error toggling agent:', error);
      return { data: null, error };
    }
  },

  getTierRequirement(agentIndex) {
    if (agentIndex < 2) return { tier: 'free', label: 'Free' };
    if (agentIndex < 4) return { tier: 'draftiq_plus', label: 'DraftIQ+' };
    return { tier: 'enterprise', label: 'Enterprise' };
  },

  canActivateAgent(currentTier, requiredTier) {
    const tierOrder = ['free', 'draftiq_plus', 'enterprise'];
    const currentIndex = tierOrder?.indexOf(currentTier);
    const requiredIndex = tierOrder?.indexOf(requiredTier);
    return currentIndex >= requiredIndex;
  },

  getUsageImpact(agentId) {
    const impacts = {
      'manager': 'High - Central coordination hub for all agents',
      'marketing-ops': 'Medium - Marketing campaigns and content creation',
      'personal-productivity': 'Medium - Task management and checklists',
      'feedback-analytics': 'Low - Analytics and feedback analysis',
      'onboarding': 'Low - Initial setup and configuration'
    };
    return impacts?.[agentId] || 'Unknown impact';
  },

  async getAgentAnalytics(userId, agentId = null) {
    try {
      // Get current month for period filter
      const currentDate = new Date();
      const currentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      
      let query = supabase
        ?.from('agent_usage_metrics')
        ?.select('agent_id, metric_type, metric_value, efficiency_score, period_month, created_at')
        ?.eq('user_id', userId);
      
      if (agentId) {
        query = query?.eq('agent_id', agentId);
      }
      
      const { data: metrics, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;

      // Process analytics data
      const analyticsMap = new Map();
      
      metrics?.forEach(metric => {
        const agentKey = metric?.agent_id;
        if (!analyticsMap?.has(agentKey)) {
          analyticsMap?.set(agentKey, {
            agentId: agentKey,
            monthlyInteractions: 0,
            efficiencyScores: [],
            adoptionData: []
          });
        }
        
        const agentData = analyticsMap?.get(agentKey);
        
        // Count monthly interactions
        if (metric?.metric_type === 'interaction') {
          agentData.monthlyInteractions += Number(metric?.metric_value || 0);
        }
        
        // Collect efficiency scores
        if (metric?.efficiency_score !== null && metric?.efficiency_score !== undefined) {
          agentData?.efficiencyScores?.push(Number(metric?.efficiency_score));
        }
        
        // Track adoption over time
        agentData?.adoptionData?.push({
          date: metric?.created_at,
          value: Number(metric?.metric_value || 0)
        });
      });

      // Calculate final analytics
      const analytics = Array.from(analyticsMap?.values())?.map(agent => {
        // Calculate average efficiency
        const avgEfficiency = agent?.efficiencyScores?.length > 0
          ? agent?.efficiencyScores?.reduce((sum, score) => sum + score, 0) / agent?.efficiencyScores?.length
          : 0;
        
        // Calculate adoption trend (comparing last 7 days vs previous 7 days)
        const sortedAdoption = agent?.adoptionData?.sort((a, b) => new Date(b?.date) - new Date(a?.date));
        const last7Days = sortedAdoption?.slice(0, 7)?.reduce((sum, d) => sum + d?.value, 0);
        const prev7Days = sortedAdoption?.slice(7, 14)?.reduce((sum, d) => sum + d?.value, 0);
        const trendPercentage = prev7Days > 0 ? ((last7Days - prev7Days) / prev7Days) * 100 : 0;
        
        return {
          agentId: agent?.agentId,
          monthlyInteractions: agent?.monthlyInteractions,
          efficiencyPercentage: Math.round(avgEfficiency),
          adoptionTrend: {
            percentage: Math.round(trendPercentage * 10) / 10,
            direction: trendPercentage > 0 ? 'up' : trendPercentage < 0 ? 'down' : 'stable'
          }
        };
      });

      return { data: analytics, error: null };
    } catch (error) {
      console.error('Error getting agent analytics:', error);
      return { data: null, error };
    }
  }
};

export default agentManagementService;